/*
 * Spinlab şemasının giriş noktası.
 *
 * Kod ve stil **yalnızca şema seçiliyken** yüklenir (React.lazy + ayrı
 * `spinlab.css`): diğer şemaların ve yönetim panelinin paketine girmez.
 *
 * Hata davranışı deterministiktir: Spinlab seçiliyken başka bir şemanın
 * kabuğu **hiçbir zaman** çizilmez. Kabuk çökerse hata kaydedilir ve tek
 * başına duran bir hata ekranı gösterilir (yeniden dene); bir sayfa çökerse
 * kabuk ayakta kalır ve yalnızca içerik alanı hata durumunu çizer
 * (bkz. SpinlabBoundary ve SpinlabShell içindeki sayfa sınırı).
 */
import { useLanguage } from './i18n';
import React, { Suspense, lazy } from 'react';
import { SpinlabBoundary } from './SpinlabBoundary';

export { SpinlabBoundary };

const Shell = lazy(() => import('./SpinlabShell'));
const Home = lazy(() => import('./SpinlabHome'));
const RoutePage = lazy(() => import('./SpinlabPages').then((m) => ({ default: m.SpinlabRoutePage })));

export const SPINLAB_THEME_ID = 'spinlab';

const BACKGROUND = '#0b0f1a';

const Blank = () => <div style={{ position: 'fixed', inset: 0, background: BACKGROUND }} />;

/** Kabuk: PublicLayout şema Spinlab iken bunu çizer. */
const isNative = (children) => React.isValidElement(children) && Boolean(children.type?.spinlabNative);

export const SpinlabLayout = ({ children, extras, persistent = false }) => {
  // Spinlab'ın kendi sayfaları (`spinlabNative`) temanın stiliyle çizilir;
  // sitenin ortak sayfaları `.spl-foreign` adasında kendi stiliyle durur.
  // Kalıcı kabukta (rotaların üstünde kurulduğunda) bu ayrımı her sayfa
  // kendisi yapar (`SpinlabPage`).
  const native = persistent || isNative(children);
  // Dil değişince kabuk ve içindeki sayfa yeniden kurulur (sayfa yenilenmez,
  // adres değişmez); metinler i18n.js'in geçerli dilinden okunur.
  const lang = useLanguage();
  return (
    <SpinlabBoundary scope="shell">
      <Suspense fallback={<Blank />}>
        <Shell key={lang} native={native} extras={extras}>{children}</Shell>
      </Suspense>
    </SpinlabBoundary>
  );
};

/** Kalıcı kabuğun içinde bir rota: ortak sayfa `.spl-foreign` adasında durur. */
export const SpinlabPage = ({ children }) => (isNative(children) ? children : (
  <div className="mirror-wrap"><div className="mirror-content"><div className="spl-foreign">{children}</div></div></div>
));

export const SpinlabHome = () => <Suspense fallback={null}><Home /></Suspense>;
SpinlabHome.spinlabNative = true;

export const SpinlabRoute = () => <Suspense fallback={null}><RoutePage /></Suspense>;
SpinlabRoute.spinlabNative = true;

const LegacyAccount = lazy(() => import('./SpinlabPages').then((m) => ({ default: m.SpinlabLegacyAccount })));
/** Ortak hesap sayfası Spinlab'ın hesap çerçevesinde (`/account/deposit` …). */
export const SpinlabAccountFrame = ({ children }) => <Suspense fallback={null}><LegacyAccount>{children}</LegacyAccount></Suspense>;
SpinlabAccountFrame.spinlabNative = true;
